import { useCallback } from 'react';
import { toast } from 'react-toastify';
import { skipToken } from '@reduxjs/toolkit/query';
import { trackError, toTrackedError } from '@src/core/sentry';
import {
  useGetDroneFlightDispatcherQuery,
  useUpdateDroneFirmwareMutation,
} from '@src/features/fleet/redux/api';
import { getDroneVersionLabel, getFirmwareBannerView } from '../utils/firmware';
import { isInitialLoad } from '../utils/query-state';

export type { FirmwareBannerView, FirmwareBannerTone } from '../utils/firmware';

interface UseDroneFirmwareOptions {
  droneId: number | undefined;
  enabled: boolean;
  canUpdate: boolean;
}

export function useDroneFirmware({ droneId, enabled, canUpdate }: UseDroneFirmwareOptions) {
  const {
    currentData: flightDispatcher,
    isLoading,
    isFetching,
    isError,
  } = useGetDroneFlightDispatcherQuery(enabled && droneId != null ? droneId : skipToken);
  const [updateFirmware, { isLoading: isUpdating }] = useUpdateDroneFirmwareMutation();

  const handleFirmwareUpdate = useCallback(async () => {
    if (!enabled || !canUpdate || droneId == null) return;

    try {
      await updateFirmware({ droneId }).unwrap();
      toast.success('Firmware update has been started');
    } catch (err: unknown) {
      trackError(toTrackedError(err), { code: 'FE_0021', extra: { flow: 'updateFirmware' } });
    }
  }, [canUpdate, droneId, enabled, updateFirmware]);

  const isFirmwareLoading = isInitialLoad({ isLoading, isFetching }, flightDispatcher);
  const bannerView = getFirmwareBannerView(flightDispatcher, { canUpdate });

  return {
    bannerView,
    droneVersion: getDroneVersionLabel({ flightDispatcher, isError }),
    handleFirmwareUpdate,
    isUpdating,
    isLoading: isFirmwareLoading,
  };
}
